/**
 * Conteo Diario — Verificación rápida del inventario.
 *
 * A diferencia del Conteo Semanal, este módulo NO aplica ajustes al inventario.
 * Solo registra el conteo físico declarado y lo compara con el stock del sistema
 * en ese momento, mostrando las diferencias para auditoría.
 *
 * Características:
 * - 1 conteo por fecha (re-guardar reemplaza el anterior).
 * - Tabla histórica de conteos recientes con sus diferencias.
 * - Indicadores visuales: verde si todo cuadra, rojo si hay variación.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import {
  ClipboardCheck,
  ClipboardList,
  Save,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getConteosDiarios,
  getConteoDiarioPorFecha,
  getConteoDiarioPreview,
  upsertConteoDiario,
  deleteConteoDiario,
} from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { EGG_LABELS } from './constants';
import { formatDate } from '../../lib/dateUtils';

const TIPOS = ['grande', 'mediano', 'pequeño', 'doble', 'sucio', 'casquiado'];
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('es-ES') : '0');
const todayIso = () => new Date().toISOString().split('T')[0];

const emptyConteo = () => TIPOS.reduce((acc, t) => ({ ...acc, [t]: '' }), {});

export const ConteoDiarioTab = () => {
  const { isConteoCiego } = useAuth();
  // Cuando el usuario está en Modo Conteo Ciego oculta las columnas
  // "Stock Sistema" y "Diferencia" al capturar (solo ve el input físico).
  // El registro guardado sigue calculando la diferencia internamente para
  // reportes/histórico, pero no la muestra en pantalla mientras cuenta.
  const canVerSistema = !isConteoCiego;
  const [fecha, setFecha] = useState(todayIso());
  const [conteos, setConteos] = useState(emptyConteo);
  const [sistema, setSistema] = useState({}); // Snapshot del sistema (referencia)
  const [observaciones, setObservaciones] = useState('');
  const [loadingForm, setLoadingForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lista, setLista] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  // Cargar sistema (stock al cierre de esa fecha) y conteo existente de la fecha
  const loadForFecha = useCallback(async (fechaIso) => {
    setLoadingForm(true);
    try {
      // Stock del sistema al cierre de `fechaIso` (histórico si es fecha pasada,
      // stock actual si es hoy). El backend decide en base al parámetro.
      const previewRes = await getConteoDiarioPreview({ fecha: fechaIso });
      const sis = previewRes.data?.sistema || {};
      setSistema(sis);

      // ¿Hay conteo previo para esta fecha?
      try {
        const existing = await getConteoDiarioPorFecha(fechaIso);
        const row = existing.data;
        const map = emptyConteo();
        const raw = row.conteos || {};
        TIPOS.forEach((t) => {
          const alt = t === 'pequeño' ? 'pequeno' : t;
          const v = raw[t] || raw[alt];
          map[t] = v?.fisico != null ? String(v.fisico) : '';
        });
        setConteos(map);
        setObservaciones(row.observaciones || '');
        setEditingId(row.id);
      } catch (err) {
        // 404 → no hay conteo previo, formulario en blanco
        if (err.response?.status !== 404) {
          console.error('Error consultando conteo:', err);
        }
        setConteos(emptyConteo());
        setObservaciones('');
        setEditingId(null);
      }
    } catch (err) {
      console.error('Error loading preview:', err);
      toast.error('No se pudo cargar el stock del sistema');
    } finally {
      setLoadingForm(false);
    }
  }, []);

  const loadLista = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await getConteosDiarios({ limit: 60 });
      setLista(r.data || []);
    } catch (err) {
      console.error('Error lista conteos:', err);
      toast.error('No se pudo cargar el histórico');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadForFecha(fecha); }, [fecha, loadForFecha]);
  useEffect(() => { loadLista(); }, [loadLista]);

  // Diferencias en vivo (físico − sistema)
  const diferenciasLive = useMemo(() => {
    return TIPOS.reduce((acc, t) => {
      const tieneInput = conteos[t] !== '';
      const fisico = parseInt(conteos[t] || 0, 10) || 0;
      const sis = parseInt(sistema[t] || 0, 10) || 0;
      acc[t] = {
        fisico,
        sistema: sis,
        diferencia: fisico - sis,
        tieneInput,
      };
      return acc;
    }, {});
  }, [conteos, sistema]);

  // Suma solo las diferencias donde el usuario realmente capturó un valor.
  const totalDiferenciaAbs = useMemo(
    () => TIPOS.reduce(
      (s, t) => (diferenciasLive[t].tieneInput ? s + Math.abs(diferenciasLive[t].diferencia) : s),
      0,
    ),
    [diferenciasLive],
  );

  // Cuántos tipos tienen valor capturado (para mostrar progreso)
  const tiposCapturados = useMemo(
    () => TIPOS.filter((t) => diferenciasLive[t].tieneInput).length,
    [diferenciasLive],
  );

  const handleChange = (tipo, value) => {
    if (value === '') {
      setConteos((p) => ({ ...p, [tipo]: '' }));
      return;
    }
    const clean = value.replace(/[^0-9]/g, '');
    setConteos((p) => ({ ...p, [tipo]: clean }));
  };

  const handleSubmit = async () => {
    if (!fecha) { toast.error('Selecciona una fecha'); return; }
    setSaving(true);
    try {
      const payload = {
        fecha,
        conteos: TIPOS.reduce((acc, t) => {
          acc[t] = parseInt(conteos[t] || 0, 10) || 0;
          return acc;
        }, {}),
        observaciones: observaciones?.trim() || null,
      };
      const r = await upsertConteoDiario(payload);
      const action = r.data?.action;
      toast.success(action === 'updated' ? 'Conteo actualizado' : 'Conteo registrado');
      await Promise.all([loadForFecha(fecha), loadLista()]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo guardar el conteo');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    try {
      await deleteConteoDiario(deleteCandidate.id);
      toast.success('Conteo eliminado');
      setDeleteCandidate(null);
      await loadLista();
      // Si borramos el de la fecha actual del form, recargar
      if (deleteCandidate.fecha === fecha) loadForFecha(fecha);
    } catch (err) {
      toast.error('No se pudo eliminar el conteo');
    }
  };

  const toggleRow = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-6" data-testid="conteo-diario-wrapper">
      {/* ============== FORMULARIO ============== */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              Conteo Diario · Verificación de Inventario
              {editingId && (
                <Badge variant="outline" className="ml-2 font-normal text-amber-700 border-amber-300 bg-amber-50">
                  Editando conteo existente
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span>Este conteo <strong>NO modifica</strong> el inventario, solo verifica.</span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Fila de fecha + observaciones */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Fecha del conteo</Label>
              <Input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                max={todayIso()}
                className="h-10"
                data-testid="conteo-diario-fecha"
              />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label className="text-xs text-muted-foreground">Observaciones (opcional)</Label>
              <Input
                type="text"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Notas del responsable, contexto del conteo..."
                className="h-10"
                maxLength={500}
                data-testid="conteo-diario-observaciones"
              />
            </div>
          </div>

          {/* Tabla de tipos */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="text-left py-2 pr-3 font-medium">Tipo</th>
                  {canVerSistema && (
                    <th className="text-right py-2 px-3 font-medium">Stock Sistema</th>
                  )}
                  <th className="text-right py-2 px-3 font-medium">Físico (contado)</th>
                  {canVerSistema && (
                    <th className="text-right py-2 px-3 font-medium">Diferencia</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {TIPOS.map((t) => {
                  const d = diferenciasLive[t];
                  const sin = d.diferencia === 0;
                  const tieneInput = conteos[t] !== '';
                  return (
                    <tr key={t} className="border-b border-border/30">
                      <td className="py-2 pr-3 font-medium">{EGG_LABELS[t === 'pequeño' ? 'pequeno' : t]}</td>
                      {canVerSistema && (
                        <td className="text-right py-2 px-3 tabular-nums text-muted-foreground">
                          {fmt(d.sistema)}
                        </td>
                      )}
                      <td className="text-right py-2 px-3">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={conteos[t]}
                          onChange={(e) => handleChange(t, e.target.value)}
                          placeholder="0"
                          className="h-9 w-32 ml-auto text-right tabular-nums"
                          data-testid={`conteo-diario-input-${t === 'pequeño' ? 'pequeno' : t}`}
                        />
                      </td>
                      {canVerSistema && (
                        <td
                          className={`text-right py-2 px-3 tabular-nums font-semibold ${
                            !tieneInput
                              ? 'text-muted-foreground/50'
                              : sin
                                ? 'text-green-700'
                                : 'text-red-700'
                          }`}
                          data-testid={`conteo-diario-dif-${t === 'pequeño' ? 'pequeno' : t}`}
                        >
                          {!tieneInput
                            ? '—'
                            : d.diferencia > 0
                              ? `+${fmt(d.diferencia)}`
                              : d.diferencia < 0
                                ? fmt(d.diferencia)
                                : '✓ Cuadra'}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Resumen + acciones */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
            <div className="flex items-center gap-2 text-sm">
              {tiposCapturados === 0 ? (
                <Badge variant="outline" className="font-normal gap-1 text-muted-foreground">
                  <ClipboardList className="w-3.5 h-3.5" />
                  Captura los valores físicos
                </Badge>
              ) : !canVerSistema ? (
                <Badge variant="outline" className="gap-1 text-primary border-primary/30 bg-primary/5">
                  <ClipboardCheck className="w-3.5 h-3.5" />
                  {tiposCapturados}/6 tipos capturados
                </Badge>
              ) : totalDiferenciaAbs === 0 ? (
                <Badge variant="outline" className="gap-1 text-green-700 border-green-300 bg-green-50">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Sin diferencias en {tiposCapturados}/6 tipos
                </Badge>
              ) : (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Diferencia: {fmt(totalDiferenciaAbs)} unidades · {tiposCapturados}/6 tipos
                </Badge>
              )}
              {loadingForm && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Cargando…
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => loadForFecha(fecha)}
                disabled={loadingForm || saving}
                className="h-9"
                data-testid="conteo-diario-refresh"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingForm ? 'animate-spin' : ''}`} />
                Refrescar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving || loadingForm}
                className="h-9"
                data-testid="conteo-diario-guardar"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando…' : editingId ? 'Actualizar conteo' : 'Guardar conteo'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============== HISTÓRICO ============== */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Histórico de Conteos Diarios
            </CardTitle>
            <Badge variant="outline" className="font-normal">
              {lista.length} conteo(s)
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {lista.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{loadingList ? 'Cargando…' : 'No hay conteos diarios registrados aún'}</p>
            </div>
          ) : (
            <Table
              wrapperClassName="max-h-[calc(100vh-420px)] rounded-md border border-border/40"
              data-testid="tabla-conteos-diarios"
            >
              <TableHeader className="sticky top-0 z-30 bg-card shadow-[0_2px_4px_-1px_rgba(0,0,0,0.08)] [&_tr]:border-b-0 [&_th]:border-b [&_th]:border-border">
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Fecha</TableHead>
                  {TIPOS.map((t) => (
                    <TableHead key={t} className="text-right">
                      {EGG_LABELS[t === 'pequeño' ? 'pequeno' : t]}
                    </TableHead>
                  ))}
                  {canVerSistema && (
                    <TableHead className="text-center">Estado</TableHead>
                  )}
                  <TableHead className="text-right w-24">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((c) => {
                  const isOpen = !!expanded[c.id];
                  const raw = c.conteos || {};
                  // Calcular diferencia total y por tipo
                  let totalDif = 0;
                  const porTipo = {};
                  TIPOS.forEach((t) => {
                    const alt = t === 'pequeño' ? 'pequeno' : t;
                    const v = raw[t] || raw[alt] || {};
                    porTipo[t] = {
                      fisico: Number(v.fisico || 0),
                      sistema: Number(v.sistema || 0),
                      diferencia: Number(v.diferencia || 0),
                    };
                    totalDif += Math.abs(porTipo[t].diferencia);
                  });
                  const tieneDif = totalDif > 0;
                  return (
                    <React.Fragment key={c.id}>
                      <TableRow
                        className={`cursor-pointer transition-colors ${
                          tieneDif && canVerSistema ? 'bg-red-50/60 hover:bg-red-100/50' : 'hover:bg-accent/40'
                        }`}
                        onClick={() => toggleRow(c.id)}
                        data-testid={`conteo-diario-row-${c.fecha}`}
                      >
                        <TableCell className="text-muted-foreground">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </TableCell>
                        <TableCell className="font-medium">{formatDate(c.fecha)}</TableCell>
                        {TIPOS.map((t) => {
                          const v = porTipo[t];
                          const sin = v.diferencia === 0;
                          return (
                            <TableCell
                              key={t}
                              className={`text-right tabular-nums ${sin || !canVerSistema ? '' : 'text-red-700 font-semibold'}`}
                              title={canVerSistema ? `Sistema: ${fmt(v.sistema)} · Físico: ${fmt(v.fisico)}` : `Físico: ${fmt(v.fisico)}`}
                            >
                              {fmt(v.fisico)}
                            </TableCell>
                          );
                        })}
                        {canVerSistema && (
                          <TableCell className="text-center">
                            {tieneDif ? (
                              <Badge variant="destructive" className="gap-1 h-6 text-[11px]">
                                <AlertTriangle className="w-3 h-3" />
                                Dif {fmt(totalDif)}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="gap-1 h-6 text-[11px] text-green-700 border-green-300 bg-green-50">
                                <CheckCircle2 className="w-3 h-3" />
                                OK
                              </Badge>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); setDeleteCandidate(c); }}
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                            data-testid={`conteo-diario-delete-${c.fecha}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Detalle expandido */}
                      {isOpen && (
                        <TableRow className="bg-muted/30 hover:bg-muted/40">
                          <TableCell colSpan={TIPOS.length + (canVerSistema ? 4 : 3)} className="p-0">
                            <div className="px-4 py-3">
                              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                                Detalle del conteo · {formatDate(c.fecha)}
                              </p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-muted-foreground border-b">
                                      <th className="text-left py-1.5 pr-3 font-medium">Tipo</th>
                                      {canVerSistema && (
                                        <th className="text-right py-1.5 px-2 font-medium">Stock Sistema</th>
                                      )}
                                      <th className="text-right py-1.5 px-2 font-medium">Físico</th>
                                      {canVerSistema && (
                                        <th className="text-right py-1.5 px-2 font-medium">Diferencia</th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {TIPOS.map((t) => {
                                      const v = porTipo[t];
                                      const sin = v.diferencia === 0;
                                      return (
                                        <tr
                                          key={t}
                                          className={`border-b border-border/30 last:border-b-0 ${sin || !canVerSistema ? '' : 'bg-red-50/40'}`}
                                        >
                                          <td className="py-1.5 pr-3 font-medium">{EGG_LABELS[t === 'pequeño' ? 'pequeno' : t]}</td>
                                          {canVerSistema && (
                                            <td className="text-right py-1.5 px-2 tabular-nums">{fmt(v.sistema)}</td>
                                          )}
                                          <td className="text-right py-1.5 px-2 tabular-nums">{fmt(v.fisico)}</td>
                                          {canVerSistema && (
                                            <td
                                              className={`text-right py-1.5 px-2 tabular-nums font-semibold ${
                                                sin ? 'text-green-700' : 'text-red-700'
                                              }`}
                                            >
                                              {v.diferencia === 0
                                                ? '0'
                                                : v.diferencia > 0
                                                  ? `+${fmt(v.diferencia)}`
                                                  : fmt(v.diferencia)}
                                            </td>
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              {c.observaciones && (
                                <p className="mt-2 text-xs text-muted-foreground italic">
                                  📝 {c.observaciones}
                                </p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirmación de borrado */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => !o && setDeleteCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar conteo diario</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar el conteo del <strong>{deleteCandidate && formatDate(deleteCandidate.fecha)}</strong>?
              <br />
              Esta acción no afecta el inventario, solo borra el registro de verificación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conteo-diario-delete-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="conteo-diario-delete-confirmar"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConteoDiarioTab;
